/**
 * Hook for real-time GPS tracking via backend WebSocket proxy
 * Backend handles authentication with Traccar and proxies position updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import Constants from 'expo-constants';
import type { LivePosition } from '../types/gps';
import { getItem } from '../lib/storage';

interface UseGPSTrackingOptions {
  enabled?: boolean;
  companyId?: number | null;
  onPositionUpdate?: (position: LivePosition) => void;
  onPositionsUpdate?: (positions: LivePosition[]) => void;
  onError?: (error: string) => void;
}

export interface UseGPSTrackingReturn {
  positions: LivePosition[];
  isConnected: boolean;
  error: string | null;
  requestPositions: () => void;
  syncDevice: (deviceId: number) => void;
  connect: () => void;
  disconnect: () => void;
}

/**
 * Get WebSocket URL from API base URL
 * Uses the same pattern as api/config.ts to ensure consistency
 */
function getWebSocketURL(): string {
  // Use the same pattern as api/config.ts - prefer EXPO_PUBLIC_API_URL, fallback to expoConfig
  const API_BASE_URL =
    process.env.EXPO_PUBLIC_API_URL ||
    (Constants.expoConfig?.extra as any)?.API_BASE_URL ||
    process.env.EXPO_PUBLIC_APP_DOMAIN ||
    (Constants.expoConfig?.extra as any)?.APP_DOMAIN ||
    'https://app.alldrivers.pt';
  
  // Ensure protocol is present
  let apiUrl = API_BASE_URL;
  if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
    apiUrl = `https://${apiUrl}`;
  }
  
  // Convert HTTP(S) to WS(S)
  let wsUrl = apiUrl.replace('https://', 'wss://').replace('http://', 'ws://');
  
  // Add WebSocket path
  return `${wsUrl}/ws/gps/tracking/`;
}

/**
 * Hook for real-time GPS tracking
 */
export function useGPSTracking(options: UseGPSTrackingOptions = {}): UseGPSTrackingReturn {
  const {
    enabled = true,
    companyId = null,
    onPositionUpdate,
    onPositionsUpdate,
    onError,
  } = options;

  const [positions, setPositions] = useState<LivePosition[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const isConnectingRef = useRef(false); // Prevent multiple simultaneous connection attempts
  const connectionAttemptRef = useRef(0); // Invalidates async connects after context changes
  const devicesMapRef = useRef<Map<number, any>>(new Map()); // Map of traccarDeviceId -> device info

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      // Handle different message types
      if (data.type === 'error') {
        console.error('[GPS WS] Server error:', data.message);
        setError(data.message);
        onError?.(data.message);
        return;
      }

      if (data.type === 'pong') {
        // Heartbeat response
        return;
      }

      // Update devices map if provided
      if (data.devices && Array.isArray(data.devices)) {
        const authorizedDevices = new Map<number, any>();
        data.devices.forEach((device: any) => {
          const authorizedDeviceId = device.traccarDeviceId ?? device.id;
          if (authorizedDeviceId != null) {
            authorizedDevices.set(authorizedDeviceId, device);
          }
        });
        devicesMapRef.current = authorizedDevices;
      }

      // Helper to create LivePosition from position data and device info
      const createLivePosition = (p: any): LivePosition => {
        const deviceInfo = devicesMapRef.current.get(p.deviceId);
        
        // Normalize vehicle data - handle both camelCase and snake_case
        let vehicle = null;
        if (deviceInfo?.vehicle) {
          vehicle = {
            id: deviceInfo.vehicle.id,
            registration_number: deviceInfo.vehicle.registration_number || deviceInfo.vehicle.registrationNumber,
            make: deviceInfo.vehicle.make || null,
            model: deviceInfo.vehicle.model || null,
            year: deviceInfo.vehicle.year || null,
          };
        }
        
        return {
          device_id: p.deviceId, // Traccar ID for position matching
          django_device_id: deviceInfo?.id, // Django ID for API calls
          device_label: deviceInfo?.label || `Device ${p.deviceId}`,
          device_imei: deviceInfo?.imei || '',
          latitude: p.latitude,
          longitude: p.longitude,
          speed: p.speed || 0,
          course: p.course || 0,
          timestamp: p.deviceTime || new Date().toISOString(),
          is_online: deviceInfo?.isOnline ?? true,
          vehicle: vehicle,
          driver: null,
        };
      };

      // Handle Traccar position updates
      if (data.positions && Array.isArray(data.positions)) {
        const newPositions: LivePosition[] = data.positions
          .filter((position: any) => devicesMapRef.current.has(position.deviceId))
          .map(createLivePosition);
        setPositions(newPositions);
        onPositionsUpdate?.(newPositions);
      } else if (data.positions) {
        // Single position update
        if (!devicesMapRef.current.has(data.positions.deviceId)) {
          return;
        }
        const updatedPosition = createLivePosition(data.positions);

        setPositions(prev => {
          const index = prev.findIndex(pos => pos.device_id === updatedPosition.device_id);
          if (index >= 0) {
            const newPositions = [...prev];
            newPositions[index] = updatedPosition;
            return newPositions;
          }
          return [...prev, updatedPosition];
        });

        onPositionUpdate?.(updatedPosition);
      }
    } catch (err) {
      console.error('[GPS WS] Error parsing message:', err);
      setError('Failed to parse GPS data');
    }
  }, [onPositionUpdate, onPositionsUpdate, onError]);

  // Connect to backend WebSocket
  const connect = useCallback(async () => {
    if (!enabled) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    if (isConnectingRef.current) {
      return; // Already attempting to connect
    }

    isConnectingRef.current = true;
    const connectionAttempt = ++connectionAttemptRef.current;

    try {
      // Get authentication token
      const token = await getItem('userToken');
      if (connectionAttempt !== connectionAttemptRef.current) {
        return;
      }
      if (!token) {
        console.error('[GPS WS] No auth token found');
        setError('Authentication required');
        onError?.('Authentication required');
        isConnectingRef.current = false;
        return;
      }

      // Build WebSocket URL with token
      const companyQuery = companyId == null
        ? ''
        : `&company_id=${encodeURIComponent(String(companyId))}`;
      const wsUrl = `${getWebSocketURL()}?token=${encodeURIComponent(token)}${companyQuery}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false; // Reset connecting flag

        // Request initial positions
        ws.send(JSON.stringify({ type: 'request_positions' }));
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        handleMessage(event);
      };

      ws.onerror = (event) => {
        if (wsRef.current !== ws) return;
        console.error('[GPS WS] WebSocket error:', event);
        console.error('[GPS WS] WebSocket readyState:', ws.readyState);
        console.error('[GPS WS] WebSocket URL:', wsUrl.replace(/\?token=[^&]+/, '?token=***'));
        
        // Provide more specific error messages based on readyState
        let errorMessage = 'Connection error';
        if (ws.readyState === WebSocket.CLOSED) {
          errorMessage = 'Connection closed unexpectedly. Check server configuration and CORS settings.';
        } else if (ws.readyState === WebSocket.CONNECTING) {
          errorMessage = 'Connection timeout. Server may be unreachable or WebSocket not configured.';
        }
        
        setError(errorMessage);
        onError?.(errorMessage);
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        setIsConnected(false);
        isConnectingRef.current = false; // Reset connecting flag

        // Provide specific error messages based on close code
        let errorMessage: string | null = null;
        if (event.code === 1006) {
          // Abnormal closure - connection was closed without a close frame
          errorMessage = 'Connection closed abnormally. This usually indicates a server-side rejection (CORS, authentication, or server configuration issue).';
          console.error('[GPS WS] Abnormal closure detected. Possible causes:');
          console.error('  - Origin not allowed (check ALLOWED_HOSTS)');
          console.error('  - Authentication failed (check token)');
          console.error('  - Server WebSocket not configured correctly');
          console.error('  - Network/firewall blocking connection');
        } else if (event.code === 1000) {
          // Normal closure - don't set error
          errorMessage = null;
        } else if (event.code === 4001) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (event.code === 4003) {
          errorMessage = 'Access denied. GPS license required or company not found.';
        } else if (event.code >= 4000 && event.code < 5000) {
          errorMessage = `Connection rejected by server (code: ${event.code}). ${event.reason || 'Check server logs for details.'}`;
        }

        if (errorMessage) {
          setError(errorMessage);
          onError?.(errorMessage);
        }

        // Attempt to reconnect if not a normal closure and still enabled
        if (event.code !== 1000 && enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          const finalError = errorMessage || 'Failed to connect after multiple attempts';
          setError(finalError);
          onError?.(finalError);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      if (connectionAttempt !== connectionAttemptRef.current) {
        return;
      }
      console.error('[GPS WS] Failed to create WebSocket:', err);
      setError('Failed to create WebSocket connection');
      onError?.('Failed to create WebSocket connection');
      isConnectingRef.current = false; // Reset connecting flag on error
    }
  }, [companyId, enabled, handleMessage, onError]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    connectionAttemptRef.current += 1;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setPositions([]);
    devicesMapRef.current.clear();
    isConnectingRef.current = false; // Reset connecting flag
  }, []);

  // Request current positions
  const requestPositions = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_positions' }));
    }
  }, []);

  // Sync specific device (placeholder for compatibility)
  const syncDevice = useCallback((deviceId: number) => {
    requestPositions();
  }, [requestPositions]);

  // Connect/disconnect based on enabled state
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, companyId]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(heartbeatInterval);
  }, [isConnected]);

  return {
    positions,
    isConnected,
    error,
    requestPositions,
    syncDevice,
    connect,
    disconnect,
  };
}

export default useGPSTracking;
